import styled from '@emotion/styled';
import { Button, Typography } from '@material-ui/core';
import { createElement, FC, Fragment } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { CreationDetailFragment, RequestState } from '../../../generated/graphql';
import { toJSDate } from '../../../graphql/datetime';
import theme from '../../../theme';
import { SPACING_REGULAR } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import mq from '../../styleUtility/mediaquery';
import HelpAnchor from '../compound/HelpAnchor';

interface Props {
  creation: CreationDetailFragment;
  isSelectedLatestDeliverable: boolean;
  modificationRequestState: RequestState | null;
  onClickRequestButton: () => void;
  className?: string;
  isPointRequired: boolean;
}

const ModificationRequestStartCard: FC<Props> = ({
  creation,
  isSelectedLatestDeliverable,
  modificationRequestState,
  onClickRequestButton,
  className,
  isPointRequired,
}) => {
  // 商品設定
  const basicMerchandise = creation.creativeFormat.placement.basicMerchandises.find(
    (basicMerchandise) => basicMerchandise.creativeType.key == creation.creativeFormat.creativeType.key,
  );
  // 修正依頼の可不可設定(価格設定が存在しない場合は修正依頼可能)
  const isModificationRequestable = basicMerchandise ? basicMerchandise.isModificationRequestable : true;

  return !isModificationRequestable ? (
    <Root className={className}>
      <Description>
        <Expired>
          <FormattedMessage id="modification_request_creating_card.disable_message" />
        </Expired>
      </Description>
    </Root>
  ) : creation.modificationRequestExpired ? (
    <Root className={className}>
      <Description>
        <Expired>
          <FormattedMessage id="modification_request_creating_card.expired_at_message" />
        </Expired>
      </Description>
    </Root>
  ) : (
    <Root className={className}>
      <Description>
        <Meta variant="subtitle1">
          {!creation.isNextModificationRequestForAFee &&
          creation.modificationRequestFreeExpiredAt &&
          typeof creation.modificationRequestFreeLimit == 'number' ? (
            <FormattedDate
              value={toJSDate(creation.modificationRequestFreeExpiredAt)}
              year="numeric"
              month="2-digit"
              day="2-digit"
              hour="2-digit"
              minute="2-digit"
            >
              {(dateTime: string) =>
                isPointRequired ? (
                  <>
                    <FormattedMessage id="modification_request_creating_card.expire_message" />
                    <ExpireCount>
                      <FormattedMessage
                        id="modification_request_creating_card.expire_count"
                        values={{
                          count: creation.modificationRequestFreeLimit,
                        }}
                      />
                    </ExpireCount>
                    <FormattedMessage
                      id="modification_request_creating_card.expire_at"
                      values={{
                        dateTime: dateTime,
                      }}
                    />
                  </>
                ) : (
                  <FormattedMessage
                    id="modification_request_creating_card.expire_message_for_global"
                    values={{ dateTime }}
                  />
                )
              }
            </FormattedDate>
          ) : null}
        </Meta>

        <Typography gutterBottom={true} color="error">
          {creation.isNextModificationRequestForAFee ? (
            <FormattedMessage id="modification_request_creating_card.for_a_fee" />
          ) : null}
        </Typography>

        <Typography gutterBottom={true}>
          {modificationRequestState === 'given_feedback' ? (
            <FormattedMessage id="modification_request_start_card.description.given_feedback" />
          ) : (
            <FormattedMessage id="modification_request_start_card.description" />
          )}
        </Typography>

        <Typography gutterBottom={true}>
          <FormattedMessage
            id="modification_request.help"
            values={{
              help_link: <HelpAnchor url="https://help.kaizen-ad.com/ja/articles/3637650">ヘルプページ</HelpAnchor>,
            }}
          />
        </Typography>
      </Description>

      <ButtonArea>
        <Button
          variant="contained"
          color="inherit"
          onClick={onClickRequestButton}
          disabled={!isSelectedLatestDeliverable}
        >
          <FormattedMessage id="modification_request_start_card.request" />
        </Button>
      </ButtonArea>
    </Root>
  );
};

const Root = styled('div')(
  gridLayoutContainer({
    columns: ['4fr', `${SPACING_REGULAR}px`, '1fr'],
    rows: ['auto'],
    areas: [['description', '.', 'button']],
  }),
  {
    paddingBottom: SPACING_REGULAR,
    [mq('sp')]: {
      ...gridLayoutContainer({
        columns: ['1fr'],
        rows: ['auto', '8px', 'auto'],
        areas: [['description'], ['.'], ['button']],
      }),
    },
  },
);

const Description = styled('div')(gridLayoutItem('description'));

const Meta = styled(Typography)({
  marginBottom: SPACING_REGULAR,
  fontWeight: 700,
});

const Expired = styled(Typography)({
  padding: `${SPACING_REGULAR}px 0`,
});

const ButtonArea = styled('div')({
  ...gridLayoutItem('button', { justifySelf: 'flex-end' }),
  ...flexibleLayoutContainer({
    alignItems: 'center',
  }),
});

const ExpireCount = styled('span')({
  color: theme.palette.primary.main,
  fontWeight: 700,
});

export default ModificationRequestStartCard;
